import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Eye, EyeOff, LogOut, KeyRound, Copy, Ban, UserCheck, Pencil, ImagePlus, ChevronUp, ChevronDown, CalendarClock, Send, X } from "lucide-react";
import { API, formatApiError } from "@/lib/api";

const TOKEN_KEY = "w2w_admin_token";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", image: "", published: true });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drills, setDrills] = useState([]);
  const [drillForm, setDrillForm] = useState({ title: "", subtitle: "", body: "", image: "", active: true, kind: "series" });
  const [drillSaving, setDrillSaving] = useState(false);
  const [drillEditingId, setDrillEditingId] = useState(null);
  const [drillUploading, setDrillUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [testSendingId, setTestSendingId] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [notice, setNotice] = useState("");

  const blankDrillForm = { title: "", subtitle: "", body: "", image: "", active: true, kind: "series" };

  const blankForm = { title: "", excerpt: "", content: "", image: "", published: true };

  const imgSrc = (img) => (img && !img.startsWith("http") && !img.startsWith("/") ? `${API}/files/${img}` : img);

  useEffect(() => {
    document.title = "Admin — Wired 2 Win Golf";
  }, []);

  const load = () => {
    axios.get(`${API}/admin/posts`, authHeader()).then(({ data }) => setPosts(data)).catch(() => setToken(null));
    axios.get(`${API}/admin/drills`, authHeader()).then(({ data }) => setDrills(data)).catch(() => {});
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await axios.put(`${API}/admin/posts/${editingId}`, form, authHeader());
      } else {
        await axios.post(`${API}/admin/posts`, form, authHeader());
      }
      setForm(blankForm);
      setEditingId(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({ title: p.title, excerpt: p.excerpt, content: p.content, image: p.image, published: p.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(blankForm);
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, authHeader());
      setForm((f) => ({ ...f, image: data.path }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
    e.target.value = "";
  };

  const togglePublish = async (p) => {
    await axios.put(`${API}/admin/posts/${p.id}`, { title: p.title, excerpt: p.excerpt, content: p.content, image: p.image, published: !p.published }, authHeader());
    load();
  };

  const saveDrill = async (e) => {
    e.preventDefault();
    setDrillSaving(true);
    setError("");
    try {
      if (drillEditingId) {
        await axios.put(`${API}/admin/drills/${drillEditingId}`, drillForm, authHeader());
      } else {
        await axios.post(`${API}/admin/drills`, drillForm, authHeader());
      }
      setDrillForm(blankDrillForm);
      setDrillEditingId(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDrillSaving(false);
    }
  };

  const startEditDrill = (d) => {
    setDrillEditingId(d.id);
    setDrillForm({ title: d.title, subtitle: d.subtitle || "", body: d.body, image: d.image || "", active: d.active, kind: d.kind || "series" });
  };

  const moveDrill = async (d, direction) => {
    await axios.post(`${API}/admin/drills/${d.id}/move`, { direction }, authHeader());
    load();
  };

  const removeDrill = async (d) => {
    if (!window.confirm(`Delete drill "${d.title}"? Subscribers partway through will skip to the next one.`)) return;
    await axios.delete(`${API}/admin/drills/${d.id}`, authHeader());
    load();
  };

  const uploadDrillImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDrillUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, authHeader());
      setDrillForm((f) => ({ ...f, image: data.path }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDrillUploading(false);
    }
    e.target.value = "";
  };

  const previewDrill = async (d) => {
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/drills/${d.id}/preview`, {}, authHeader());
      setPreviewHtml(data.html);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const testSendDrill = async (d) => {
    setTestSendingId(d.id);
    setError("");
    setNotice("");
    try {
      const { data } = await axios.post(`${API}/admin/drills/${d.id}/test-send`, { to: testEmail }, authHeader());
      setNotice(`Test email sent to ${data.to} — check that inbox (subject starts with [TEST]).`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setTestSendingId(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await axios.delete(`${API}/admin/posts/${p.id}`, authHeader());
    load();
  };

  const [batches, setBatches] = useState([]);
  const [codeForm, setCodeForm] = useState({ org: "", quantity: 10, tier: "member", expires_days: "" });
  const [newCodes, setNewCodes] = useState(null);
  const [codeBusy, setCodeBusy] = useState(false);

  const loadCodes = () => {
    axios.get(`${API}/admin/codes`, authHeader()).then(({ data }) => setBatches(data)).catch(() => {});
  };

  useEffect(() => {
    if (token) loadCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const createBatch = async (e) => {
    e.preventDefault();
    setCodeBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/codes`, {
        org: codeForm.org,
        quantity: Number(codeForm.quantity) || 10,
        tier: codeForm.tier,
        expires_days: codeForm.expires_days ? Number(codeForm.expires_days) : null,
      }, authHeader());
      setNewCodes(data);
      setCodeForm({ org: "", quantity: 10, tier: "member", expires_days: "" });
      loadCodes();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCodeBusy(false);
    }
  };

  const deactivate = async (batchId) => {
    if (!window.confirm("Deactivate this batch? Unredeemed codes will stop working.")) return;
    await axios.post(`${API}/admin/codes/${batchId}/deactivate`, {}, authHeader());
    loadCodes();
  };

  const copyCodes = (codes) => {
    navigator.clipboard.writeText(codes.join("\n")).catch(() => {});
  };

  const [coaches, setCoaches] = useState([]);
  const [coachName, setCoachName] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [coachBusy, setCoachBusy] = useState(false);

  const loadCoaches = () => {
    axios.get(`${API}/admin/coaches`, authHeader()).then(({ data }) => setCoaches(data)).catch(() => {});
  };

  useEffect(() => {
    if (token) loadCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addCoach = async (e) => {
    e.preventDefault();
    setCoachBusy(true);
    setError("");
    try {
      await axios.post(`${API}/admin/coaches`, { name: coachName, email: coachEmail || undefined }, authHeader());
      setCoachName("");
      setCoachEmail("");
      loadCoaches();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCoachBusy(false);
    }
  };

  const resendWelcome = async (id) => {
    await axios.post(`${API}/admin/coaches/${id}/resend-welcome`, {}, authHeader());
  };

  const toggleCoach = async (id) => {
    await axios.post(`${API}/admin/coaches/${id}/toggle`, {}, authHeader());
    loadCoaches();
  };

  const payCoach = async (id) => {
    if (!window.confirm("Mark all pending commissions for this coach as paid?")) return;
    await axios.post(`${API}/admin/coaches/${id}/pay-all`, {}, authHeader());
    loadCoaches();
  };

  const deleteReferral = async (id) => {
    if (!window.confirm("Remove this referral from the ledger?")) return;
    await axios.delete(`${API}/admin/referrals/${id}`, authHeader());
    loadCoaches();
  };

  const coachLink = (code) => `${window.location.origin}/?coach=${code}`;

  if (!token)
    return (
      <main data-testid="admin-login-page" className="min-h-[100svh] flex items-center justify-center px-6 pt-20">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-ink p-8 space-y-5" data-testid="admin-login-form">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="w-3 h-3 bg-volt" />
            <span className="font-display font-extrabold tracking-tight">ADMIN</span>
          </div>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            data-testid="admin-email-input"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            data-testid="admin-password-input"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          {error && <p data-testid="admin-login-error" className="text-red-400 text-xs">{error}</p>}
          <button type="submit" data-testid="admin-login-button"
            className="w-full bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors">
            Sign In
          </button>
        </form>
      </main>
    );

  return (
    <main data-testid="admin-dashboard" className="pt-32 pb-28 max-w-5xl mx-auto px-6">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-display font-black tracking-tighter text-4xl uppercase">Posts</h1>
        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-semibold transition-colors">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <form onSubmit={create} className="border border-white/10 bg-ink p-6 sm:p-8 space-y-4 mb-12" data-testid="admin-post-form">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-widest text-volt">{editingId ? "Editing post" : "New post"}</p>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="admin-cancel-edit"
              className="text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors">
              Cancel edit
            </button>
          )}
        </div>
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title"
          data-testid="admin-post-title"
          className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
        <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Excerpt"
          data-testid="admin-post-excerpt"
          className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL"
            data-testid="admin-post-image"
            className="flex-1 w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <label data-testid="admin-upload-label"
            className="inline-flex items-center gap-2 px-4 py-3 border border-white/15 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-volt hover:border-volt transition-colors cursor-pointer shrink-0">
            <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload image"}
            <input type="file" accept="image/*" onChange={uploadImage} data-testid="admin-upload-input" className="hidden" disabled={uploading} />
          </label>
        </div>
        {form.image && (
          <img src={imgSrc(form.image)} alt="Post preview" data-testid="admin-image-preview"
            className="h-24 object-cover border border-white/10" />
        )}
        <textarea required rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Content (blank line between paragraphs)"
          data-testid="admin-post-content"
          className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors resize-y" />
        {error && <p data-testid="admin-post-error" className="text-red-400 text-xs">{error}</p>}
        <button type="submit" disabled={saving || uploading} data-testid="admin-post-submit"
          className="bg-volt text-obsidian font-display font-bold px-6 py-3 hover:bg-white transition-colors disabled:opacity-50">
          {saving ? "Saving..." : editingId ? "Save Changes" : "Publish Post"}
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-post-list">
        {posts.map((p) => (
          <div key={p.id} className="border border-white/10 bg-ink px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-display font-bold truncate">{p.title}</p>
              <p className="text-xs text-white/40">{p.published ? "Published" : "Draft"} · /resources/{p.slug}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.slug}`} aria-label="Edit post"
                className="p-2 text-white/50 hover:text-volt transition-colors">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => togglePublish(p)} data-testid={`admin-toggle-${p.slug}`} aria-label={p.published ? "Unpublish" : "Publish"}
                className="p-2 text-white/50 hover:text-volt transition-colors">
                {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(p)} data-testid={`admin-delete-${p.slug}`} aria-label="Delete post"
                className="p-2 text-white/50 hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* WIRED WEDNESDAYS */}
      <div className="mt-16 pt-10 border-t border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <CalendarClock className="w-5 h-5 text-volt" />
          <h2 className="font-display font-black tracking-tighter text-3xl uppercase">Wired Wednesdays</h2>
        </div>
        <p className="text-white/50 text-sm mb-4 max-w-2xl">
          Load your drill series here. Every Wednesday at midday ET, each subscriber automatically gets the next tip in their
          sequence — everyone starts at No. 1, so nobody misses the early ones. A <span className="text-white">timely commentary</span> jumps
          the queue: it goes to everyone on the next Wednesday, once, and the series simply pauses that week — subscribers pick
          up exactly where they left off.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-8 max-w-2xl">
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Your email for test sends (e.g. your Gmail)"
            data-testid="admin-drill-test-email" type="email"
            className="flex-1 bg-white/5 border border-white/15 px-4 py-2.5 text-sm focus:outline-none focus:border-volt transition-colors" />
          <p className="text-white/40 text-xs shrink-0">Eye = on-screen preview · <Send className="w-3 h-3 inline" /> = send a real test to this address</p>
        </div>

        <form onSubmit={saveDrill} className="border border-white/10 bg-ink p-6 sm:p-8 space-y-4 mb-8" data-testid="admin-drill-form">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-volt">
              {drillEditingId
                ? (drillForm.kind === "timely" ? "Editing timely commentary" : `Editing drill No. ${drills.find((d) => d.id === drillEditingId)?.seq || ""}`)
                : (drillForm.kind === "timely" ? "New timely commentary — everyone gets it next Wednesday, once" : `New drill — will be No. ${drills.filter((d) => d.kind !== "timely").length + 1}`)}
            </p>
            {drillEditingId && (
              <button type="button" onClick={() => { setDrillEditingId(null); setDrillForm(blankDrillForm); }} data-testid="admin-drill-cancel-edit"
                className="text-xs font-bold uppercase tracking-widest text-white/50 hover:text-white transition-colors">
                Cancel edit
              </button>
            )}
          </div>
          <input required value={drillForm.title} onChange={(e) => setDrillForm({ ...drillForm, title: e.target.value })}
            placeholder="Drill title (e.g. The 10-Second Reset)" data-testid="admin-drill-title"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <select value={drillForm.kind} onChange={(e) => setDrillForm({ ...drillForm, kind: e.target.value })}
            data-testid="admin-drill-kind"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors text-white">
            <option value="series">Series tip — each subscriber gets it in sequence</option>
            <option value="timely">Timely commentary — everyone gets it next Wednesday, once</option>
          </select>
          <input value={drillForm.subtitle} onChange={(e) => setDrillForm({ ...drillForm, subtitle: e.target.value })}
            placeholder="Subtitle (optional — shown under the title, e.g. Why Flow States Are Fleeting)" data-testid="admin-drill-subtitle"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <textarea required rows={8} value={drillForm.body} onChange={(e) => setDrillForm({ ...drillForm, body: e.target.value })}
            placeholder="Drill content — paste straight from Word; each line keeps its own spacing. Optional formatting: **bold**, *italic*, ## subheading"
            data-testid="admin-drill-body"
            className="w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors resize-y" />
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <input value={drillForm.image} onChange={(e) => setDrillForm({ ...drillForm, image: e.target.value })} placeholder="Image URL (optional)"
              data-testid="admin-drill-image"
              className="flex-1 w-full bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
            <label data-testid="admin-drill-upload-label"
              className="inline-flex items-center gap-2 px-4 py-3 border border-white/15 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-volt hover:border-volt transition-colors cursor-pointer shrink-0">
              <ImagePlus className="w-4 h-4" /> {drillUploading ? "Uploading..." : "Upload image"}
              <input type="file" accept="image/*" onChange={uploadDrillImage} data-testid="admin-drill-upload-input" className="hidden" disabled={drillUploading} />
            </label>
          </div>
          {drillForm.image && (
            <img src={imgSrc(drillForm.image)} alt="Drill preview" data-testid="admin-drill-image-preview"
              className="h-24 object-cover border border-white/10" />
          )}
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer" data-testid="admin-drill-active-label">
            <input type="checkbox" checked={drillForm.active} onChange={(e) => setDrillForm({ ...drillForm, active: e.target.checked })}
              data-testid="admin-drill-active" className="accent-[#CCFF00] w-4 h-4" />
            Active (inactive drills are skipped in the sequence)
          </label>
          <button type="submit" disabled={drillSaving || drillUploading} data-testid="admin-drill-submit"
            className="bg-volt text-obsidian font-display font-bold px-6 py-3 hover:bg-white transition-colors disabled:opacity-50">
            {drillSaving ? "Saving..." : drillEditingId ? "Save Changes" : "Add to Series"}
          </button>
        </form>

        {notice && <p data-testid="admin-drill-notice" className="text-volt text-xs mb-4">{notice}</p>}

        <div className="space-y-3" data-testid="admin-drill-list">
          {drills.length === 0 && (
            <p className="text-white/40 text-sm" data-testid="admin-drill-empty">No drills yet — add your first one above.</p>
          )}
          {drills.map((d, i) => {
            const isTimely = d.kind === "timely";
            const seriesCount = drills.filter((x) => x.kind !== "timely").length;
            return (
            <div key={d.id} className="border border-white/10 bg-ink px-5 py-4 flex items-center justify-between gap-4" data-testid={`admin-drill-row-${isTimely ? "timely-" + d.id.slice(0, 6) : d.seq}`}>
              <div className="min-w-0 flex items-center gap-4">
                <span className={`shrink-0 font-display font-black text-sm px-2 py-1 border ${isTimely ? "text-white border-white/40" : "text-volt border-volt/40"}`}>
                  {isTimely ? "TIMELY" : `No. ${d.seq}`}
                </span>
                <div className="min-w-0">
                  <p className="font-display font-bold truncate">{d.title}</p>
                  <p className="text-xs text-white/40">
                    {isTimely
                      ? (d.active ? "Queued — everyone gets it next Wednesday, once" : d.sent_at ? `Sent ${new Date(d.sent_at).toLocaleDateString()}` : "Inactive")
                      : (d.active ? "Active" : "Inactive — skipped")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => previewDrill(d)} data-testid={`admin-drill-preview-${isTimely ? "timely" : d.seq}`} aria-label="Preview email"
                  className="p-2 text-white/50 hover:text-volt transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => testSendDrill(d)} disabled={testSendingId === d.id} data-testid={`admin-drill-test-${isTimely ? "timely" : d.seq}`} aria-label="Send test email to me"
                  className="p-2 text-white/50 hover:text-volt transition-colors disabled:opacity-40">
                  <Send className="w-4 h-4" />
                </button>
                {!isTimely && (
                  <>
                    <button onClick={() => moveDrill(d, -1)} disabled={i === 0} data-testid={`admin-drill-up-${d.seq}`} aria-label="Move up"
                      className="p-2 text-white/50 hover:text-volt transition-colors disabled:opacity-20">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveDrill(d, 1)} disabled={i === seriesCount - 1} data-testid={`admin-drill-down-${d.seq}`} aria-label="Move down"
                      className="p-2 text-white/50 hover:text-volt transition-colors disabled:opacity-20">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button onClick={() => startEditDrill(d)} data-testid={`admin-drill-edit-${isTimely ? "timely" : d.seq}`} aria-label="Edit drill"
                  className="p-2 text-white/50 hover:text-volt transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => removeDrill(d)} data-testid={`admin-drill-delete-${isTimely ? "timely" : d.seq}`} aria-label="Delete drill"
                  className="p-2 text-white/50 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* ACCESS CODES */}
      <div className="mt-16 pt-10 border-t border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <KeyRound className="w-5 h-5 text-volt" />
          <h2 className="font-display font-black tracking-tighter text-3xl uppercase">Access Codes</h2>
        </div>
        <p className="text-white/50 text-sm mb-8 max-w-2xl">
          Generate free-membership codes for partner organizations — veterans' charities, certified coaches, sponsors. Recipients redeem at /redeem.
        </p>

        <form onSubmit={createBatch} className="border border-white/10 bg-ink p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="admin-code-form">
          <input required value={codeForm.org} onChange={(e) => setCodeForm({ ...codeForm, org: e.target.value })}
            placeholder="Organization (e.g. Veterans Golf Assoc)" data-testid="admin-code-org"
            className="sm:col-span-2 bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <input type="number" min="1" max="500" value={codeForm.quantity} onChange={(e) => setCodeForm({ ...codeForm, quantity: e.target.value })}
            placeholder="Qty" data-testid="admin-code-qty"
            className="bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <select value={codeForm.tier} onChange={(e) => setCodeForm({ ...codeForm, tier: e.target.value })}
            data-testid="admin-code-tier"
            className="bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors text-white">
            <option value="member">Member access</option>
            <option value="coach">Coach access</option>
          </select>
          <input type="number" min="0" value={codeForm.expires_days} onChange={(e) => setCodeForm({ ...codeForm, expires_days: e.target.value })}
            placeholder="Expires in days (optional)" data-testid="admin-code-expiry"
            className="bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <button type="submit" disabled={codeBusy} data-testid="admin-code-submit"
            className="sm:col-span-2 lg:col-span-5 bg-volt text-obsidian font-display font-bold px-6 py-3 hover:bg-white transition-colors disabled:opacity-50">
            {codeBusy ? "Generating..." : "Generate Code Batch"}
          </button>
        </form>

        {newCodes && (
          <div className="border border-volt/40 bg-volt/5 p-6 mb-8" data-testid="admin-new-codes">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-volt">{newCodes.codes.length} codes created for {newCodes.org} — send these to your partner contact</p>
              <button onClick={() => copyCodes(newCodes.codes)} data-testid="admin-copy-codes"
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-volt transition-colors shrink-0 ml-4">
                <Copy className="w-4 h-4" /> Copy all
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto font-mono text-xs text-white/70 space-y-1">
              {newCodes.codes.map((c) => <p key={c}>{c}</p>)}
            </div>
          </div>
        )}

        <div className="space-y-3" data-testid="admin-batch-list">
          {batches.map((b) => (
            <div key={b.id} className="border border-white/10 bg-ink px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-display font-bold truncate">{b.org} <span className="text-volt text-xs uppercase tracking-widest ml-2">{b.tier}</span></p>
                <p className="text-xs text-white/40">
                  {b.redeemed}/{b.quantity} redeemed
                  {b.expires_at ? ` · expires ${new Date(b.expires_at).toLocaleDateString()}` : " · no expiry"}
                  {!b.active && " · INACTIVE"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => copyCodes(b.codes.filter((c) => !c.redeemed_by).map((c) => c.code))}
                  data-testid={`admin-batch-copy-${b.id.slice(0, 8)}`} aria-label="Copy unredeemed codes"
                  className="p-2 text-white/50 hover:text-volt transition-colors">
                  <Copy className="w-4 h-4" />
                </button>
                {b.active && (
                  <button onClick={() => deactivate(b.id)} data-testid={`admin-batch-deactivate-${b.id.slice(0, 8)}`} aria-label="Deactivate batch"
                    className="p-2 text-white/50 hover:text-red-400 transition-colors">
                    <Ban className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {batches.length === 0 && <p className="text-white/30 text-sm">No code batches yet.</p>}
        </div>
      </div>

      {/* COACHES & COMMISSIONS */}
      <div className="mt-16 pt-10 border-t border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <UserCheck className="w-5 h-5 text-volt" />
          <h2 className="font-display font-black tracking-tighter text-3xl uppercase">Coaches</h2>
        </div>
        <p className="text-white/50 text-sm mb-8 max-w-2xl">
          Certified coaches share their personal link. When a referred student joins the <span className="text-white">annual</span> plan and claims their assets, a $10 commission is logged below. Pay out monthly, then mark as paid.
        </p>

        <form onSubmit={addCoach} className="border border-white/10 bg-ink p-6 sm:p-8 flex flex-col sm:flex-row gap-4 mb-8" data-testid="admin-coach-form">
          <input required value={coachName} onChange={(e) => setCoachName(e.target.value)}
            placeholder="Coach name (e.g. Dave Bisbee)" data-testid="admin-coach-name"
            className="flex-1 bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <input type="email" value={coachEmail} onChange={(e) => setCoachEmail(e.target.value)}
            placeholder="Coach email (sends welcome email)" data-testid="admin-coach-email"
            className="flex-1 bg-white/5 border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-volt transition-colors" />
          <button type="submit" disabled={coachBusy} data-testid="admin-coach-submit"
            className="bg-volt text-obsidian font-display font-bold px-6 py-3 hover:bg-white transition-colors disabled:opacity-50">
            {coachBusy ? "Adding..." : "Add Coach"}
          </button>
        </form>

        <div className="space-y-3" data-testid="admin-coach-list">
          {coaches.map((c) => (
            <div key={c.id} className="border border-white/10 bg-ink px-5 py-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display font-bold truncate">
                    {c.name}
                    {!c.active && <span className="text-red-400 text-xs uppercase tracking-widest ml-2">inactive</span>}
                  </p>
                  <p className="text-xs text-white/40 font-mono mt-1 break-all">{coachLink(c.code)}</p>
                  <p className="text-xs text-white/50 mt-2">
                    {c.referral_count} referral{c.referral_count === 1 ? "" : "s"} ·{" "}
                    <span className="text-volt">${c.pending_total.toFixed(0)} pending</span> · ${c.paid_total.toFixed(0)} paid
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => navigator.clipboard.writeText(coachLink(c.code)).catch(() => {})}
                    data-testid={`admin-coach-copy-${c.code}`} aria-label="Copy coach link"
                    className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-volt border border-white/15 hover:border-volt transition-colors inline-flex items-center gap-2">
                    <Copy className="w-3.5 h-3.5" /> Copy link
                  </button>
                  {c.email && (
                    <button onClick={() => resendWelcome(c.id)} data-testid={`admin-coach-resend-${c.code}`}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-volt border border-white/15 hover:border-volt transition-colors">
                      Resend email
                    </button>
                  )}
                  {c.pending_total > 0 && (
                    <button onClick={() => payCoach(c.id)} data-testid={`admin-coach-pay-${c.code}`}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-widest bg-volt text-obsidian hover:bg-white transition-colors">
                      Mark paid
                    </button>
                  )}
                  <button onClick={() => toggleCoach(c.id)} data-testid={`admin-coach-toggle-${c.code}`}
                    className="px-3 py-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-red-400 border border-white/15 transition-colors">
                    {c.active ? "Deactivate" : "Activate"}
                  </button>
                </div>
              </div>
              {c.referrals.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                  {c.referrals.map((r) => (
                    <p key={r.id} className="text-[11px] text-white/40 font-mono flex items-center gap-2">
                      {r.member_email} · ${r.amount.toFixed(0)} · {r.status} · {new Date(r.created_at).toLocaleDateString()}
                      <button onClick={() => deleteReferral(r.id)} data-testid={`admin-referral-delete-${r.id.slice(0, 8)}`}
                        aria-label="Remove referral" className="text-white/30 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
          {coaches.length === 0 && <p className="text-white/30 text-sm">No coaches yet.</p>}
        </div>
      </div>

      {/* DRILL EMAIL PREVIEW MODAL */}
      {previewHtml && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)} data-testid="drill-preview-modal">
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-ink border border-white/10 border-b-0">
              <p className="text-xs font-bold uppercase tracking-widest text-volt">Email preview — as subscribers will see it</p>
              <button onClick={() => setPreviewHtml(null)} data-testid="drill-preview-close" aria-label="Close preview"
                className="p-1.5 text-white/60 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <iframe srcDoc={previewHtml} title="Drill email preview" data-testid="drill-preview-frame"
              className="w-full h-[75vh] bg-[#0A0A0A] border border-white/10" />
          </div>
        </div>
      )}
    </main>
  );
}
